<template>
  <div class="h-screen w-screen bg-[#f1f5f9] text-slate-900 font-sans antialiased overflow-hidden select-none">
    <div class="grid h-full w-full" style="grid-template-columns: minmax(0, 1fr) 420px">

      <main class="h-full bg-[#0f172a] flex flex-col relative shadow-inner">
        <FileViewer
          v-if="selectedEvidence"
          :file-id="selectedEvidence.id"
          :visible="true"
          inline
          @close="goBack"
          @delete="deleteFile"
          class="w-full h-full"
        />
        <div v-else class="flex-1 flex flex-col items-center justify-center p-8">
          <div class="w-16 h-16 bg-slate-800/80 rounded-2xl flex items-center justify-center shadow-lg border border-slate-700/50 mb-4 animate-bounce">
            <i class="ri-file-search-line text-3xl text-indigo-400"></i>
          </div>
          <h3 class="text-sm font-semibold text-slate-200 tracking-wide">Área de Visualización</h3>
          <p class="text-xs text-slate-400 text-center max-w-xs mt-1 leading-relaxed">
            Selecciona un documento del panel derecho para iniciar la validación jurídica.
          </p>
        </div>
      </main>

<aside class="flex flex-col h-screen max-h-screen bg-white border-l border-slate-200/80 shadow-2xl z-10 overflow-hidden">

  <div class="p-6 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white flex-none">
    <div class="flex items-center gap-3.5">
      <div class="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-200">
        <i class="fas fa-balance-scale text-white text-sm"></i>
      </div>
      <div>
        <h1 class="text-sm font-bold tracking-tight text-slate-900">Módulo de Evidencias</h1>
        <p class="text-[11px] text-slate-500 font-medium tracking-wide mt-0.5">Auditoría & Valoración Legal</p>
      </div>
    </div>
  </div>

  <div class="flex-1 overflow-y-auto p-6 space-y-6 min-h-0 custom-scrollbar">

    <section v-if="selectedEvidence" class="space-y-4">
      <div class="flex items-center justify-between">
        <h4 class="text-[11px] text-sm font-semibold text-gray-800">Detalles del Recurso</h4>
        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase transition-all" :class="statusTone(selectedEvidence)">
          ● {{ statusLabel(selectedEvidence) }}
        </span>
      </div>
      
      <div class="bg-slate-50/80 rounded-2xl p-4 border border-slate-100 space-y-3 shadow-sm">
        <div class="px-1">
          <span class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Título de la prueba</span>
          <span class="text-xs font-semibold text-slate-800 block mt-0.5 break-words">{{ selectedEvidence.name || "Sin nombre asignado" }}</span>
        </div>
        
        <div class="grid grid-cols-2 gap-2 border-t border-slate-200/50 pt-3 px-1">
          <div>
            <span class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Radicado en</span>
            <span class="text-xs font-medium text-slate-700 block mt-0.5">{{ formatNoteDate(selectedEvidence.createdAt) }}</span>
          </div>
          <div>
            <span class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Responsable</span>
            <span class="text-xs font-medium text-slate-700 block mt-0.5 truncate">{{ uploaderText(selectedEvidence) }}</span>
          </div>
        </div>
      </div>
    </section>

    <div v-else class="py-12 px-4 text-center border-2 border-dashed border-slate-200 bg-slate-50/50 rounded-2xl text-slate-400">
      <i class="fas fa-inbox text-2xl text-slate-300 mb-2"></i>
      <p class="text-xs font-medium">No hay ningún expediente activo en este momento.</p>
    </div>

    <section v-if="selectedEvidence" class="space-y-3">
      <h3 class="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Estado Jurídico</h3>
      
      <div class="grid grid-cols-3 gap-2">
        <button
          class="py-2.5 px-2 text-xs font-bold rounded-xl flex flex-col items-center justify-center gap-1.5 transition-all cursor-pointer bg-emerald-50 hover:bg-emerald-600 border border-emerald-200/60 hover:border-emerald-600 text-emerald-700 hover:text-white shadow-xs active:scale-95 disabled:opacity-40"
          :disabled="savingAction"
          @click="setReviewStatus('Aprobado')"
        >
          <i class="fas fa-check-circle text-sm"></i>Aprobar
        </button>
        
        <button
          class="py-2.5 px-2 text-xs font-bold rounded-xl flex flex-col items-center justify-center gap-1.5 transition-all cursor-pointer bg-amber-50 hover:bg-amber-500 border border-amber-200/60 hover:border-amber-500 text-amber-700 hover:text-white shadow-xs active:scale-95 disabled:opacity-40"
          :disabled="savingAction"
          @click="setReviewStatus('En revisión')"
        >
          <i class="fas fa-spinner text-sm"></i>En curso
        </button>
        
        <button
          class="py-2.5 px-2 text-xs font-bold rounded-xl flex flex-col items-center justify-center gap-1.5 transition-all cursor-pointer bg-rose-50 hover:bg-rose-600 border border-rose-200/60 hover:border-rose-600 text-rose-700 hover:text-white shadow-xs active:scale-95 disabled:opacity-40"
          :disabled="savingAction"
          @click="setReviewStatus('No comprobable')"
        >
          <i class="fas fa-times-circle text-sm"></i>Rechazar
        </button>
      </div>

      <div class="border border-slate-100 rounded-xl bg-slate-50/50 overflow-hidden">
        <button
          type="button"
          class="w-full px-4 py-3 flex justify-between items-center hover:bg-slate-50 transition-colors"
          @click="isAccessControlExpanded = !isAccessControlExpanded"
        >
          <span class="text-xs font-bold text-slate-700 flex items-center gap-2">
            <i class="fas fa-lock-open text-slate-400 text-[11px]"></i> Permisos de visualización
          </span>
          <i class="fas text-slate-400 text-[10px] transition-transform duration-200" :class="isAccessControlExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>

        <div v-if="isAccessControlExpanded" class="p-4 bg-white border-t border-slate-100 space-y-3.5">
          <label class="flex cursor-pointer items-center gap-3 border border-slate-100 bg-slate-50/50 px-3.5 py-2.5 rounded-xl transition hover:bg-slate-50">
            <input
              type="checkbox"
              class="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4 border-slate-300"
              :checked="selectedEvidence.sharedWithDelegates"
              @change="togglePublicVisibility"
            />
            <span class="text-xs font-medium text-slate-700">Hacer público para toda la comisión</span>
          </label>

          <div v-if="!selectedEvidence.sharedWithDelegates" class="pt-1">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Delegaciones autorizadas:</p>
            <div class="flex flex-col gap-1.5 max-h-40 overflow-y-auto pr-1">
              <div
                v-for="del in delegations"
                :key="del.id"
                @click="toggleDelegationShare(del.id)"
                class="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-100 bg-slate-50/30 cursor-pointer text-xs text-slate-600 hover:bg-slate-50 transition-all"
                :class="isSharedWith(del.id) ? 'bg-indigo-50/60 border-indigo-100 text-indigo-900 font-medium' : ''"
              >
                <span class="truncate pr-2">{{ del.name || del.delegationName || 'Sin nombre' }}</span>
                <i class="fas text-xs" :class="isSharedWith(del.id) ? 'fa-check-circle text-indigo-600' : 'fa-circle text-slate-200'"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="space-y-3">
      <div class="flex items-center justify-between">
        <h3 class="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Bitácora de Observaciones</h3>
        <span v-if="notesList(selectedEvidence).length" class="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
          {{ notesList(selectedEvidence).length }}
        </span>
      </div>

      <div v-if="notesList(selectedEvidence).length" class="space-y-2.5">
        <article
          v-for="(note, index) in notesList(selectedEvidence)"
          :key="`${index}-${note.createdAt || note.requestedBy || 'note'}`"
          class="border border-slate-100 rounded-xl overflow-hidden bg-slate-50/30 shadow-2xs"
          :class="{ 'border-slate-300/80 ring-4 ring-slate-100 bg-white': expandedNotes[index] }"
        >
          <div
            class="px-3.5 py-3 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors"
            @click="toggleNote(index)"
          >
            <div>
              <strong class="text-xs font-bold text-slate-800 block">{{ note.requestedBy || "Usuario Anónimo" }}</strong>
              <span class="text-[10px] text-slate-400 font-medium">{{ formatNoteDate(note.createdAt) }}</span>
            </div>
            <i class="fas text-[10px] text-slate-400 transition-transform duration-200" :class="expandedNotes[index] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </div>

          <div v-if="expandedNotes[index]" class="border-t border-slate-100 bg-white">
            <div class="p-3.5 text-xs text-slate-700 leading-relaxed bg-slate-50/40">
              {{ note.message || note.text || "Solicitó más información." }}
            </div>

            <div v-if="note.responses && note.responses.length" class="p-3 space-y-2 bg-white">
              <div
                v-for="(response, responseIndex) in note.responses"
                :key="`${index}-response-${responseIndex}`"
                class="bg-slate-50 p-2.5 rounded-xl border border-slate-100"
              >
                <div class="flex items-center justify-between text-[10px] text-slate-400 mb-1">
                  <span class="font-bold text-slate-600">↳ {{ response.responder || "Presidente" }}</span>
                  <span>{{ formatNoteDate(response.createdAt) }}</span>
                </div>
                <p class="m-0 text-xs text-slate-600 leading-normal">{{ response.message || response.text }}</p>
              </div>
            </div>

            <div class="p-3 bg-white border-t border-slate-100 space-y-2">
              <textarea
                v-model="replyDrafts[index]"
                class="w-full border border-slate-200 rounded-xl p-2.5 text-xs outline-none resize-none min-h-[65px] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50/50 focus:bg-white transition"
                rows="2"
                placeholder="Añadir comentario técnico..."
              ></textarea>
              <div class="flex justify-between items-center">
                <span class="text-[10px] text-slate-400 flex items-center gap-1">
                  <i class="fas fa-eye-slash text-[9px]"></i> Confidencial (Revisores)
                </span>
                <button
                  class="px-3 py-1.5 rounded-lg text-[11px] font-bold flex items-center gap-1.5 cursor-pointer transition-all disabled:opacity-40"
                  :class="replyDrafts[index] && replyDrafts[index].trim() ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm' : 'bg-slate-100 text-slate-400'"
                  :disabled="!replyDrafts[index] || !replyDrafts[index].trim() || savingAction"
                  @click.stop="replyToNote(index)"
                >
                  Enviar <i class="fas fa-paper-plane text-[9px]"></i>
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>

      <div v-else class="py-6 text-center border border-dashed border-slate-200 rounded-xl text-slate-400 text-xs">
        No hay observaciones registradas.
      </div>
    </section>
  </div>

  <div class="p-4 bg-slate-50 border-t border-slate-100 flex-none">
    <button 
      type="button"
      class="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs py-3 rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-30 disabled:pointer-events-none active:scale-98 cursor-pointer"
      :disabled="!selectedEvidence" 
      @click="downloadSelected"
    >
      <i class="fas fa-download text-slate-300"></i> Descargar Documento Original
    </button>
  </div>
</aside>
    </div>
  </div>
</template>
<script>
import { addDoc, arrayUnion, arrayRemove, collection, doc, getDoc, getDocs, updateDoc, deleteDoc } from "firebase/firestore";
import axios from "axios";
import { db } from "@/fb";
import { getEvidenceSelection, clearEvidenceSelection } from "@/evidenceReviewContext";
import FileViewer from "@/components/FileViewer.vue";

export default {
  name: "EvidenceManagerPage",
  components: { FileViewer },
  data() {
    return {
      loading: false,
      savingAction: false,
      savingNote: false,
      error: "",
      selectedEvidence: null,
      delegations: [],
      replyDrafts: {},
      expandedNotes: {},
      isAccessControlExpanded: false,
    };
  },
  created() {
    this.loadSelectedEvidence();
  },
  methods: {
    labelsText(docItem) {
      if (!docItem || !docItem.labels) {
        return "Sin etiquetas";
      }

      if (Array.isArray(docItem.labels)) {
        return docItem.labels.join(", ");
      }

      return String(docItem.labels);
    },
    uploaderText(docItem) {
      if (!docItem || !docItem.uploader) {
        return "No disponible";
      }

      return docItem.uploader.name || docItem.uploader.type || "No disponible";
    },
    notesList(docItem) {
      if (!docItem || !Array.isArray(docItem.notes)) {
        return [];
      }

      return docItem.notes;
    },
    noteAuthorName() {
      const delegateData = this.$delegateStore?.state?.delegateData || {};

      return (
        delegateData.name ||
        [delegateData.firstName, delegateData.lastName].filter(Boolean).join(" ") ||
        delegateData.email ||
        this.$delegateStore?.state?.authContext?.delegateId ||
        "Presidente"
      );
    },
    formatNoteDate(value) {
      if (!value) {
        return "Sin fecha";
      }

      if (typeof value === "string") {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleString("es-CO");
        }

        return value;
      }

      if (typeof value === "number") {
        return new Date(value).toLocaleString("es-CO");
      }

      if (value.seconds !== undefined && value.nanoseconds !== undefined) {
        return new Date(value.seconds * 1000).toLocaleString("es-CO");
      }

      if (value.toDate && typeof value.toDate === "function") {
        return value.toDate().toLocaleString("es-CO");
      }

      return "Sin fecha";
    },
    statusLabel(docItem) {
      return (docItem && (docItem.status || docItem.reviewStatus || docItem.state)) || "Pendiente";
    },
    statusTone(docItem) {
      const status = String(this.statusLabel(docItem)).toLowerCase();

      if (status.includes("aprob")) {
        return "pill-approved";
      }

      if (status.includes("rech")) {
        return "pill-rejected";
      }

      if (status.includes("revis")) {
        return "pill-review";
      }

      if (status.includes("comprob")) {
        return "pill-unprovable";
      }

      return "pill-pending";
    },
    getMediaDocRef(mediaId, authContext) {
      return doc(
        db,
        "commissions",
        authContext.cohort,
        "commissions",
        authContext.commissionId,
        "media",
        mediaId
      );
    },
    async persistMediaUpdate(nextFields = {}, noteEntry = null) {
      if (!this.selectedEvidence) {
        return;
      }

      if (!this.$delegateStore.state.loaded) {
        await this.$loadDelegateProfile();
      }

      const authContext = this.$delegateStore.state.authContext || {};
      const mediaRef = this.getMediaDocRef(this.selectedEvidence.id, authContext);
      const payload = { ...nextFields };

      if (noteEntry) {
        payload.notes = arrayUnion(noteEntry);
      }

      await updateDoc(mediaRef, payload);

      this.selectedEvidence = {
        ...this.selectedEvidence,
        ...nextFields,
        ...(noteEntry ? { notes: [...this.notesList(this.selectedEvidence), noteEntry] } : {}),
      };
    },
    async resolveDocumentUrl(fileKey) {
      if (!fileKey) {
        throw new Error("Missing fileKey");
      }

      const response = await fetch(this.buildApiDocumentUrl(fileKey));
      if (!response.ok) {
        throw new Error("Unable to resolve file URL");
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const payload = await response.json();
        return payload.url || payload.data?.url || payload.fileUrl || payload.link || "";
      }

      const text = await response.text();
      return text.trim();
    },
    buildApiDocumentUrl(fileKey) {
      const normalizedFileKey = String(fileKey || "").replace(/^\/+/, "");
      return `https://api.jgmun.com/api/docs/${encodeURI(normalizedFileKey)}`;
    },
    async loadSelectedEvidence() {
      this.loading = true;
      this.error = "";

      try {
        let selected = getEvidenceSelection();

        if (!selected && this.$route.query.evidenceId) {
          selected = { id: this.$route.query.evidenceId };
        }

        if (!selected) {
          this.selectedEvidence = null;
          return;
        }

        if (!this.$delegateStore.state.loaded) {
          await this.$loadDelegateProfile();
        }

        const authContext = this.$delegateStore.state.authContext || {};
        const mediaRef = this.getMediaDocRef(selected.id, authContext);
        const mediaSnapshot = await getDoc(mediaRef);
        const mediaData = mediaSnapshot.exists() ? mediaSnapshot.data() : {};

        this.selectedEvidence = {
          ...selected,
          ...mediaData,
          id: selected.id,
        };

        await this.loadDelegations();
      } catch (error) {
        this.error = "No se pudo cargar la evidencia seleccionada.";
      } finally {
        this.loading = false;
      }
    },
    async loadDelegations() {
      try {
        const authContext = this.$delegateStore.state.authContext || {};
        const delegatesRef = collection(
          db,
          "commissions",
          authContext.cohort,
          "commissions",
          authContext.commissionId,
          "delegates"
        );
        const snapshot = await getDocs(delegatesRef);

        const ownerId = this.selectedEvidence?.uploader?.id;
        
        // Usamos un objeto para evitar duplicados si hay múltiples delegados por delegación
        const uniqueDelegations = {};

        snapshot.docs.forEach((d) => {
          const docData = d.data();
          const del = docData.delegation;
          
          if (del && del.id && del.id !== ownerId) {
            uniqueDelegations[del.id] = { id: del.id, ...del };
          }
        });

        this.delegations = Object.values(uniqueDelegations);
      } catch (e) {
        // Error silenciado
      }
    },
    async deleteFile(file) {
      if (!file) return;
      if (!confirm('¿Estás seguro de eliminar esta evidencia permanentemente?')) return;
      
      try {
        if (file.fileKey) {
          await axios.delete(`https://api.jgmun.com/api/docs/${encodeURIComponent(file.fileKey)}`);
        }
        
        const authContext = this.$delegateStore.state.authContext || {};
        const mediaRef = this.getMediaDocRef(file.id, authContext);
        await deleteDoc(mediaRef);
        
        this.goBack();
      } catch (err) {
        this.error = "No se pudo eliminar el archivo.";
      }
    },
    setupPreview(fileUrl, type, apiUrl) {
      const normalizedType = String(type || fileUrl || "").toLowerCase();
      const extension = normalizedType.split(".").pop();
      const isOfficeDocument =
        DOC_TYPES.some((entry) => normalizedType.includes(entry)) ||
        DOC_TYPES.includes(extension);
      const isImage =
        IMAGE_TYPES.some((entry) => normalizedType.includes(entry)) ||
        IMAGE_TYPES.includes(extension);
      const isMedia =
        MEDIA_TYPES.some((entry) => normalizedType.includes(entry)) ||
        MEDIA_TYPES.includes(extension);

      if (isOfficeDocument) {
        this.viewerType = "pdf-office";
        this.viewerLabel = "API";
        this.officeViewerUrl = apiUrl || fileUrl;
        return;
      }

      if (isImage) {
        this.viewerType = "image";
        this.viewerLabel = "PhotoSwipe";
        this.$nextTick(() => {
          const imageGallery = this.$refs.imageGallery;
          if (!imageGallery) {
            return;
          }

          this.lightbox = new PhotoSwipeLightbox({
            gallery: imageGallery,
            children: "a",
            pswpModule: () => import("photoswipe"),
          });

          this.lightbox.init();
        });
        return;
      }

      if (isMedia) {
        this.viewerType = "media";
        this.viewerLabel = "Plyr";
        this.isVideo = ["mp4", "mov", "webm", "mkv"].some((entry) => normalizedType.includes(entry));
        this.$nextTick(() => {
          const mediaElement = this.$refs.mediaElement;
          if (!mediaElement) {
            return;
          }

          this.plyrInstance = new Plyr(mediaElement, {
            controls: ["play", "progress", "current-time", "mute", "volume", "settings", "fullscreen"],
          });
          mediaElement.src = fileUrl;
        });
        return;
      }

      this.viewerType = "pdf-office";
      this.viewerLabel = "API";
      this.officeViewerUrl = apiUrl || fileUrl;
    },
    toggleNote(index) {
      this.$set(this.expandedNotes, index, !this.expandedNotes[index]);
    },
    async requestMoreInfo() {
      if (!this.selectedEvidence || this.savingAction) {
        return;
      }

      this.savingAction = true;

      try {
        const note = {
          action: "request_more_info",
          message: "Solicitó más información",
          requestedBy: this.noteAuthorName(),
          requestedById: this.$delegateStore?.state?.authContext?.delegateId || "",
          createdAt: new Date().toISOString(),
        };

        await this.persistMediaUpdate({}, note);
      } catch (error) {
        this.error = "No se pudo registrar la nota en la evidencia seleccionada.";
      } finally {
        this.savingAction = false;
      }
    },
    async sendNotification(message) {
      const uploaderId = this.selectedEvidence?.uploader?.id;
      if (!uploaderId) return;

      const myId = this.$delegateStore?.state?.delegateData?.delegation?.id;
      if (uploaderId === myId) return; // Evitar auto-notificación si el presidente es el mismo uploader

      const authContext = this.$delegateStore.state.authContext || {};
      const notificationsRef = collection(db, "notifications");

      try {
        await addDoc(notificationsRef, {
          recipientId: uploaderId,
          senderName: this.noteAuthorName(),
          message,
          evidenceId: this.selectedEvidence.id,
          createdAt: new Date().toISOString(),
          read: false,
          cohort: authContext.cohort,
          commissionId: authContext.commissionId
        });
      } catch (e) {
      }
    },
    async setReviewStatus(nextStatus) {
      if (!this.selectedEvidence || this.savingAction) {
        return;
      }

      this.savingAction = true;

      try {
        const normalizedStatus = String(nextStatus || "").trim();
        const note = {
          action: "status_update",
          message: `Estado actualizado a ${normalizedStatus}`,
          requestedBy: this.noteAuthorName(),
          requestedById: this.$delegateStore?.state?.authContext?.delegateId || "",
          createdAt: new Date().toISOString(),
        };

        await this.persistMediaUpdate(
          {
            status: normalizedStatus,
            reviewStatus: normalizedStatus,
            state: normalizedStatus,
            reviewedBy: this.noteAuthorName(),
            reviewedById: this.$delegateStore?.state?.authContext?.delegateId || "",
            reviewedAt: new Date().toISOString(),
          },
          note
        );
        await this.sendNotification(`El estado de tu evidencia "${this.selectedEvidence.name}" ha sido actualizado a: ${normalizedStatus}`);
      } catch (error) {
        this.error = "No se pudo actualizar el estado del documento.";
      } finally {
        this.savingAction = false;
      }
    },
    async togglePublicVisibility() {
      if (!this.selectedEvidence || this.savingAction) return;
      this.savingAction = true;
      try {
        const newState = !this.selectedEvidence.sharedWithDelegates;
        await this.persistMediaUpdate({
          sharedWithDelegates: newState,
        });
        await this.sendNotification(newState 
          ? `Tu evidencia "${this.selectedEvidence.name}" ahora es visible para toda la comisión.` 
          : `Se ha revocado el acceso público a tu evidencia.`);
      } catch (error) {
        this.error = "No se pudo actualizar la visibilidad.";
      } finally {
        this.savingAction = false;
      }
    },
    isSharedWith(delId) {
      const shared = this.selectedEvidence?.sharedDelegations || [];
      return shared.includes(delId);
    },
    async toggleDelegationShare(delId) {
      if (!this.selectedEvidence || this.savingAction) return;
      this.savingAction = true;
      try {
        const isShared = this.isSharedWith(delId);
        const authContext = this.$delegateStore.state.authContext || {};
        const mediaRef = this.getMediaDocRef(this.selectedEvidence.id, authContext);

        if (isShared) {
          await updateDoc(mediaRef, { sharedDelegations: arrayRemove(delId) });
          this.$set(this.selectedEvidence, 'sharedDelegations', 
            (this.selectedEvidence.sharedDelegations || []).filter(id => id !== delId)
          );
        } else {
          await updateDoc(mediaRef, { sharedDelegations: arrayUnion(delId) });
          this.$set(this.selectedEvidence, 'sharedDelegations', 
            [...(this.selectedEvidence.sharedDelegations || []), delId]
          );
        }
      } catch (error) {
        this.error = "Error al actualizar permisos por delegación.";
      } finally {
        this.savingAction = false;
      }
    },
    async replyToNote(index) {
      if (!this.selectedEvidence || this.savingAction) {
        return;
      }

      const draft = (this.replyDrafts[index] || "").trim();
      if (!draft) {
        return;
      }

      this.savingAction = true;

      try {
        const notes = this.notesList(this.selectedEvidence).map((note) => ({
          ...note,
          responses: Array.isArray(note.responses) ? [...note.responses] : [],
        }));

        if (!notes[index]) {
          return;
        }

        notes[index].responses.push({
          responder: this.noteAuthorName(),
          responderId: this.$delegateStore?.state?.authContext?.delegateId || "",
          message: draft,
          createdAt: new Date().toISOString(),
        });

        await this.persistMediaUpdate({ notes });
        await this.sendNotification(`Nueva respuesta en tu evidencia: "${this.selectedEvidence.name}"`);
        this.$set(this.replyDrafts, index, "");
      } catch (error) {
        this.error = "No se pudo responder la nota seleccionada.";
      } finally {
        this.savingAction = false;
      }
    },
    downloadSelected() {
      if (!this.selectedEvidence) {
        return;
      }

      const fileKey = this.selectedEvidence.fileKey;
      if (!fileKey) {
        return;
      }

      this.resolveDocumentUrl(fileKey)
        .then((fileUrl) => {
          const link = document.createElement("a");
          link.href = fileUrl;
          link.target = "_blank";
          link.rel = "noopener";
          link.download = this.selectedEvidence.name || "evidence-file";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          this.error = "No se pudo descargar el archivo seleccionado.";
        });
    },
    goBack() {
      clearEvidenceSelection();
      this.$router.push("/evidence");
    },
  },
};
</script>


